import React, { useEffect, useState } from "react";
import { Button, Dropdown, Flex, Tooltip } from "@fluentui/react-northstar";
import { OpenOutsideIcon, PollIcon } from "@fluentui/react-icons-northstar";
import find from "lodash/find";
import filter from "lodash/filter";

import { useMe, useProjects } from "../../queries";
import IProject from "../../interfaces/IProject";
import { getVacationReportLink } from "../../common/textOperations";
import { dovolenaProject } from "../../constants";

export interface IProjectPickerProps {
    viewOnlyOwn: boolean;
    selectedProjectId: string;
    projectSelected: (projectId: string) => void;
}

const ProjectPicker: React.FunctionComponent<IProjectPickerProps> = ({ selectedProjectId, viewOnlyOwn, projectSelected }) => {
    const [searchQuery, setSearchQuery] = useState<string>();
    const [searchOpened, setSearchOpened] = useState(false);
    const [isVacation, setIsVacation] = useState(false);

    const { data: me } = useMe();
    const { data: projectsData } = useProjects();
    const projects: IProject[] = projectsData ?? [];
    const login = me?.login ?? "";
    const vacationReportLink = me ? getVacationReportLink(me.dataverseUserId) : undefined;

    const projectsForDropdown = projects.map((x: IProject) => ({ header: x.subject, key: x.id }));

    // The dropdown has to know the selected item, otherwise it clears the search query when it is closed
    const selectedItem = find(projectsForDropdown, (x: any) => x.key === selectedProjectId) ?? null;

    useEffect(() => {
        const foundProject = find(projects, (x: IProject) => x.id === selectedProjectId);
        if (foundProject) {
            setSearchQuery(foundProject.subject);
            setSearchOpened(false);
            setIsVacation(foundProject.subject === dovolenaProject);
        }
    }, [selectedProjectId, projectsData]);

    const searchChanged = (_: any, data: any) => {
        if (!data.searchQuery && selectedProjectId) {
            projectSelected("");
        }

        if (data.searchQuery !== searchQuery) {
            setSearchQuery(data.searchQuery);
        }
    };

    const searchOpenChanged = (_: any, data: any) => {
        setSearchOpened(data.open);
    };

    const projectChanged = (_: any, data: any) => {
        const projectId = data.value?.key?.toString();
        if (projectId) {
            projectSelected(projectId);
        }
    };

    const search = (_: any[], query: string) => {
        const lower = query.toLowerCase();
        const words = lower.split(" ");
        let filtered: IProject[] = projects;

        if (viewOnlyOwn && login) {
            filtered = filter(filtered, (x: IProject) => x.owner && x.owner.login === login);
        }

        filtered = filter(filtered, (x: IProject) => {
            const subject = x.subject.toLowerCase();

            // tslint:disable-next-line: no-shadowed-variable
            const match: boolean[] = words.map((_: any) => false);
            for (let index = 0; index < words.length; index++) {
                match[index] = subject.indexOf(words[index]) > -1;
            }

            return match.filter((m: boolean) => m).length === match.length;
        });

        return filtered.map((x: IProject) => ({ header: x.subject, key: x.id }));
    };

    const projectLink = () => {
        return import.meta.env.VITE_CRM_BASE_URL + `&pagetype=entityrecord&etn=incident&id=${selectedProjectId}`;
    };

    return (
        <Flex gap="gap.smaller">
            <Flex.Item grow>
                <Dropdown
                    items={projectsForDropdown}
                    value={selectedItem}
                    open={searchOpened}
                    onOpenChange={searchOpenChanged}
                    onChange={projectChanged}
                    searchQuery={searchQuery}
                    onSearchQueryChange={searchChanged}
                    search={search}
                    fluid
                />
            </Flex.Item>

            {isVacation && vacationReportLink ?
                <Tooltip
                    trigger={<Button text iconOnly icon={<PollIcon outline />} onClick={() => window.open(vacationReportLink)} />}
                    position-align="before-center"
                    content="Otevřít report dovolené"
                />
                :
                <Tooltip
                    trigger={<Button text iconOnly icon={<OpenOutsideIcon />} disabled={!selectedProjectId} onClick={() => window.open(projectLink())} />}
                    position-align="before-center"
                    content="Otevřít případ v CRM"
                />
            }
        </Flex>
    );
};

export default ProjectPicker;
